import React, { useEffect, useRef, useState } from "react";
import { fetchPlayerAchievements, type PlayerAchievement } from "../utils/homeApi";
import { ACHIEVEMENT_CATALOG, LEVEL_NAMES } from "../utils/achievement-catalog";

type PlayerAchievementsProps = {
  playerId: number;
};

export default function PlayerAchievements({ playerId }: PlayerAchievementsProps) {
  const [items, setItems] = useState<PlayerAchievement[]>([]);

  useEffect(() => {
    let alive = true;
    setItems([]);
    fetchPlayerAchievements(playerId)
      .then((response) => {
        if (alive) {
          setItems(response.items);
        }
      })
      .catch((error) => {
        console.error("Error loading player achievements:", error);
      });

    return () => {
      alive = false;
    };
  }, [playerId]);

  return <AchievementShelf items={items} />;
}

export function AchievementShelf({ items }: { items: PlayerAchievement[] }) {
  const [openCode, setOpenCode] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Tiered achievements first, highest level first; milestones everyone has go last.
  const shown = items
    .filter((item) => ACHIEVEMENT_CATALOG[item.code] !== undefined)
    .sort((left, right) => rankOf(right) - rankOf(left));
  const openItem = shown.find((item) => item.code === openCode);

  useEffect(() => {
    if (!openCode) {
      return;
    }

    function closeOnOutsideClick(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenCode(null);
      }
    }

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpenCode(null);
      }
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openCode]);

  if (shown.length === 0) {
    return null;
  }

  return (
    <div className="achievements" ref={containerRef} aria-label="Osiągnięcia">
      {shown.map((item) => {
        const entry = ACHIEVEMENT_CATALOG[item.code]!;

        return (
          <button
            key={item.code}
            type="button"
            className={`achievements__medal achievements__medal--${medalTone(item)}`}
            aria-expanded={openCode === item.code}
            aria-label={`${titleOf(item)} — pokaż szczegóły`}
            onClick={() => setOpenCode(openCode === item.code ? null : item.code)}
          >
            <span aria-hidden="true">{entry.symbol}</span>
          </button>
        );
      })}

      {openItem && <AchievementBubble item={openItem} />}
    </div>
  );
}

function AchievementBubble({ item }: { item: PlayerAchievement }) {
  const entry = ACHIEVEMENT_CATALOG[item.code]!;
  const reached = item.levels[item.levels.length - 1]!;

  return (
    <span className="achievements__bubble" role="status">
      <strong className="achievements__title">{titleOf(item)}</strong>
      <span className="achievements__line">{entry.describe(reached.threshold)}</span>
      <span className="achievements__line achievements__line--muted">
        Zdobyte {new Date(reached.earnedAt).toLocaleDateString("pl-PL")}
      </span>
      {item.nextThreshold !== null && (
        <span className="achievements__line achievements__line--muted">
          Do poziomu {LEVEL_NAMES[item.levels.length]}: {item.value}/{item.nextThreshold}
        </span>
      )}
    </span>
  );
}

function titleOf(item: PlayerAchievement): string {
  const entry = ACHIEVEMENT_CATALOG[item.code]!;

  if (entry.milestone) {
    return entry.label;
  }

  return `${entry.label} · ${LEVEL_NAMES[item.levels.length - 1]}`;
}

function medalTone(item: PlayerAchievement): string {
  if (ACHIEVEMENT_CATALOG[item.code]!.milestone) {
    return "milestone";
  }

  return `level-${item.levels.length}`;
}

function rankOf(item: PlayerAchievement): number {
  if (ACHIEVEMENT_CATALOG[item.code]!.milestone) {
    return 0;
  }

  return item.levels.length;
}
